import React, { useEffect, useRef } from 'react';
import './Home.scss';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  MerchGrid,
  MerchGridItem,
  LoadingSpinner,
  LoadingSpinnerContainer,
  ErrorBox,
} from '@amzn/burberry-ui-components';
import { awsRum } from 'src/rum';
import { metricEmitter } from 'src/utils/metricEmitter';
import { AppDispatch } from 'src/store/store';
import { selectTemplate } from 'src/store/templateSlice';
import { fetchCollection, selectCollections, selectGetCollectionInProgress, selectMerchError } from 'src/store/merchSlice';
import { hydrateCatalog, selectCatalog, selectHydrateCatalogComplete, selectCatalogError } from 'src/store/catalogSlice';
import { selectCsrfToken } from 'src/store/authSlice';
import { getStage } from 'src/utils/roadieHelper';
import { getActiveEngagementId } from 'src/utils/engagement';
import { buildProductUrl, UH_OH, GENERIC_ERROR_STRING } from '@amzn/d2c-application-website-common';

const PAGE_NAME = 'Home';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const template = useSelector(selectTemplate);
  const collections = useSelector(selectCollections);
  const getCollectionInProgress = useSelector(selectGetCollectionInProgress);
  const merchError = useSelector(selectMerchError);
  const catalog = useSelector(selectCatalog);
  const hydrateCatalogComplete = useSelector(selectHydrateCatalogComplete);
  const catalogError = useSelector(selectCatalogError);
  const csrfToken = useSelector(selectCsrfToken);

  const pageLoadTime = useRef(Date.now());
  const pageLoadEmitted = useRef(false);
  const engagementId = getActiveEngagementId(template);
  const merchItems = engagementId ? (collections?.[engagementId] ?? []) : [];

  // Page view metric
  useEffect(() => {
    awsRum?.recordEvent('PageView', { page: PAGE_NAME });
  }, []);

  // Fetch merch collection
  useEffect(() => {
    if (engagementId) {
      dispatch(fetchCollection({ engagementId, stage: getStage(), csrfToken }));
    }
  }, [dispatch, engagementId, csrfToken]);

  // TODO: Remove this once we switch to ListMerchV2
  // Hydrate catalog from merch items
  const merchAsinKey = merchItems.map((item: any) => item.itemAsin).join(',');
  useEffect(() => {
    if (merchItems.length > 0) {
      const identifiers = merchItems.map((item: any) => ({ itemAsin: item.itemAsin }));
      dispatch(hydrateCatalog({
        itemIdentifiers: identifiers,
        stage: getStage(),
        locale: 'en-US',
        csrfToken,
      }));
    }
  }, [dispatch, merchAsinKey, csrfToken]);

  // Page load metric
  useEffect(() => {
    if (!pageLoadEmitted.current && hydrateCatalogComplete && catalog && catalog.length > 0) {
      pageLoadEmitted.current = true;
      const loadTime = Date.now() - pageLoadTime.current;
      awsRum?.recordEvent('PageLoad', { page: PAGE_NAME, loadTimeMs: String(loadTime) });
    }
  }, [hydrateCatalogComplete, catalog]);

  // Filter and sort catalog items to match merch order
  const getDisplayItems = () => {
    if (!catalog || !merchItems.length) return [];
    const merchAsins = merchItems.map((m: any) => m.itemAsin);
    return catalog
      .filter((item: any) => merchAsins.includes(item.identifier?.itemAsin))
      .sort((a: any, b: any) =>
        merchAsins.indexOf(a.identifier?.itemAsin) - merchAsins.indexOf(b.identifier?.itemAsin),
      );
  };

  // Loading state
  if (getCollectionInProgress || (!hydrateCatalogComplete && merchItems.length > 0)) {
    return (
      <div className="home-page home-page--loading" data-testid="home-page">
        <LoadingSpinnerContainer>
          <LoadingSpinner />
        </LoadingSpinnerContainer>
      </div>
    );
  }

  // Error state
  if ((merchError && merchItems.length === 0) || (catalogError && hydrateCatalogComplete && (!catalog || catalog.length === 0))) {
    return (
      <div className="home-page" data-testid="home-page">
        <ErrorBox
          heading={UH_OH}
          message={GENERIC_ERROR_STRING}
          metricEmitter={metricEmitter}
        />
      </div>
    );
  }

  const displayItems = getDisplayItems();

  return (
    <div className="home-page" data-testid="home-page">
      <MerchGrid
        merchGridColumns={template?.merchGridColumns}
        merchGridSpacing={template?.merchGridSpacing}
      >
        {displayItems.map((item: any) => (
          <MerchGridItem
            key={item.identifier?.itemAsin ?? item.preferredAsin}
            imageUrl={item.images?.[0]?.highRes?.url ?? item.images?.[0]?.lowRes?.url ?? ''}
            imageAlt={item.images?.[0]?.altText}
            title={item.name}
            price={item.price}
            onItemClick={() => navigate(buildProductUrl(item.identifier?.itemAsin))}
            metricEmitter={metricEmitter}
          />
        ))}
      </MerchGrid>
    </div>
  );
};

export default Home;
